import React, { useState } from "react";

import { Card } from "@/client/components/ui";
import { NumberSpinner } from "@/client/components/ui/NumberSpinner";
import {
  AddIcon,
  CheckIcon,
  DeleteIcon,
  DinnerIcon,
  EditIcon,
  EmptyIcon,
  LunchIcon,
  UnplannedIcon,
} from "@/client/icons";
import {
  dateStrForDayIndex,
  DAYS_ES,
  getDayDate,
  StockMap,
  UnplannedConsumptionRow,
} from "@/client/pages/plan/types";

function RecipeChip({
  recipe,
  plannedServings,
  stockMap,
  consumedServings,
  needsStock,
  onRemove,
  onConsume,
  onChangeServings,
}: {
  recipe: { recipe_id: string; recipe_name: string; recipe_type: string };
  plannedServings: number;
  stockMap: StockMap;
  consumedServings: number;
  needsStock?: Set<string>;
  onRemove: () => void;
  onConsume: () => void;
  onChangeServings: (delta: number) => void;
}) {
  const info = stockMap.get(recipe.recipe_id);
  const stock = info?.servings ?? 0;
  const fullyConsumed = consumedServings >= plannedServings && consumedServings > 0;
  const overConsumed = consumedServings > plannedServings;
  const hasConsumed = consumedServings > 0;
  const noStock = needsStock?.has(recipe.recipe_id) && stock < plannedServings && !hasConsumed;
  return (
    <div
      className={`group relative rounded-lg border transition-colors px-2.5 py-1.5 ${
        overConsumed
          ? "bg-term-accent/8 border-term-accent/25"
          : fullyConsumed
            ? "bg-term-user/8 border-term-user/25"
            : hasConsumed
              ? "bg-term-user/5 border-term-user/20"
              : noStock
                ? "bg-term-error/5 border-term-error/25"
                : "bg-term-bg border-term-border/60 hover:border-term-border"
      }`}
    >
      {/* Mobile: always-visible pill button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        className="md:hidden absolute -top-1.5 -right-1.5 w-5 h-5 flex items-center justify-center rounded-full bg-term-raised border border-term-border text-term-text-dim text-sm leading-none cursor-pointer hover:text-term-error hover:border-term-error/30 transition-colors"
        aria-label="Quitar"
      >
        ×
      </button>
      {/* Desktop: hidden until hover */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        className="hidden md:block absolute top-1 right-1 text-transparent group-hover:text-term-text-muted hover:!text-term-error cursor-pointer transition-colors text-micro leading-none"
        aria-label="Quitar"
      >
        ×
      </button>
      <div className="flex items-center gap-1.5 pr-3">
        <span
          className={`text-sm leading-snug ${hasConsumed ? "text-term-text-dim" : "text-term-text"}`}
        >
          {recipe.recipe_name}
        </span>
      </div>
      <div className="flex items-center gap-2 mt-1">
        {/* Servings +/- */}
        <span onClick={(e) => e.stopPropagation()}>
          <NumberSpinner
            value={plannedServings}
            onDecrement={() => onChangeServings(-1)}
            onIncrement={() => onChangeServings(1)}
            size="sm"
          />
        </span>
        {/* Consumption status */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onConsume();
          }}
          className={`inline-flex items-center gap-0.5 cursor-pointer transition-all text-xs leading-none py-0.5 ${
            overConsumed
              ? "text-term-accent font-medium"
              : fullyConsumed
                ? "text-term-user font-medium"
                : hasConsumed
                  ? "text-term-user"
                  : "text-term-text-muted hover:text-term-user"
          }`}
          title={
            hasConsumed ? `${consumedServings}/${plannedServings} consumidas` : "Registrar consumo"
          }
        >
          {hasConsumed ? (
            <>
              {fullyConsumed && !overConsumed ? (
                <CheckIcon
                  width={10}
                  height={10}
                  className="shrink-0"
                  strokeWidth={2}
                  aria-hidden
                />
              ) : (
                <EmptyIcon width={10} height={10} className="shrink-0" aria-hidden />
              )}
              <span>
                {consumedServings}/{plannedServings}
              </span>
            </>
          ) : (
            <EmptyIcon width={10} height={10} className="shrink-0 opacity-55" aria-hidden />
          )}
        </button>
        {!hasConsumed && stock > 0 && (
          <span className="text-micro text-term-text-muted ml-auto">{stock} disp.</span>
        )}
        {noStock && (
          <span className="text-micro text-term-error font-medium ml-auto">sin stock</span>
        )}
      </div>
    </div>
  );
}

export function DayCard({
  day,
  dayIndex,
  startDate,
  isToday,
  stockMap,
  consumptionMap,
  lunchRecipes,
  dinnerRecipes,
  unplannedConsumptions,
  lunchNote,
  dinnerNote,
  needsStock,
  onAdd,
  onRemove,
  onConsume,
  onChangeServings,
  onSetNote,
  onDeleteConsumptions,
}: {
  day: string;
  dayIndex: number;
  startDate: string;
  isToday: boolean;
  stockMap: StockMap;
  consumptionMap: Map<string, number>;
  lunchRecipes: { recipe_id: string; servings: number; recipe_name: string; recipe_type: string }[];
  dinnerRecipes: {
    recipe_id: string;
    servings: number;
    recipe_name: string;
    recipe_type: string;
  }[];
  unplannedConsumptions: UnplannedConsumptionRow[];
  lunchNote: string | null;
  dinnerNote: string | null;
  needsStock?: Set<string>;
  onAdd: (day: string, mealType: "lunch" | "dinner") => void;
  onRemove: (day: string, mealType: "lunch" | "dinner", recipeId: string) => void;
  onConsume: (day: string, mealType: "lunch" | "dinner", recipeId: string) => void;
  onChangeServings: (
    day: string,
    mealType: "lunch" | "dinner",
    recipeId: string,
    delta: number,
  ) => void;
  onSetNote: (day: string, mealType: "lunch" | "dinner", note: string | null) => void;
  onDeleteConsumptions: (consumptionIds: string[]) => void;
}) {
  const dayNum = getDayDate(startDate, dayIndex);
  const dateStr = dateStrForDayIndex(startDate, dayIndex);

  function getConsumed(recipeId: string) {
    return consumptionMap.get(`${recipeId}:${dateStr}`) ?? 0;
  }

  function MealSection({
    mealLabel,
    mealType,
    recipes,
    note,
  }: {
    mealLabel: string;
    mealType: "lunch" | "dinner";
    recipes: { recipe_id: string; servings: number; recipe_name: string; recipe_type: string }[];
    note: string | null;
  }) {
    const MealIcon = mealType === "lunch" ? LunchIcon : DinnerIcon;
    const [editingNote, setEditingNote] = useState(false);
    const [noteValue, setNoteValue] = useState(note ?? "");

    function saveNote() {
      const trimmed = noteValue.trim();
      onSetNote(day, mealType, trimmed || null);
      setEditingNote(false);
    }

    return (
      <div>
        <div className="text-xs font-medium text-term-text-muted uppercase tracking-wide mb-1.5 flex items-center gap-1.5">
          <MealIcon width={12} height={12} className="shrink-0 opacity-80" aria-hidden />
          {mealLabel}
        </div>
        <div className="flex flex-col gap-1.5">
          {recipes.map((r) => (
            <RecipeChip
              key={r.recipe_id}
              recipe={r}
              plannedServings={r.servings}
              stockMap={stockMap}
              consumedServings={getConsumed(r.recipe_id)}
              needsStock={needsStock}
              onRemove={() => onRemove(day, mealType, r.recipe_id)}
              onConsume={() => onConsume(day, mealType, r.recipe_id)}
              onChangeServings={(delta) => onChangeServings(day, mealType, r.recipe_id, delta)}
            />
          ))}
          {/* Slot note */}
          {editingNote ? (
            <div className="flex flex-col gap-1">
              <input
                value={noteValue}
                onChange={(e) => setNoteValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") saveNote();
                  if (e.key === "Escape") setEditingNote(false);
                }}
                autoFocus
                className="text-sm text-term-text-dim bg-term-bg border border-term-border rounded-lg px-2.5 py-1.5 focus:border-term-accent-dim focus:outline-none"
                placeholder="Ej: comemos afuera"
              />
              <div className="flex gap-1">
                <button
                  onClick={saveNote}
                  className="text-sm text-term-accent cursor-pointer py-0.5 px-1"
                >
                  guardar
                </button>
                <button
                  onClick={() => setEditingNote(false)}
                  className="text-sm text-term-text-muted cursor-pointer py-0.5 px-1"
                >
                  cancelar
                </button>
              </div>
            </div>
          ) : note ? (
            <button
              onClick={() => {
                setNoteValue(note);
                setEditingNote(true);
              }}
              className="text-sm text-term-text-muted italic text-left px-1 py-0.5 hover:text-term-text-dim cursor-pointer transition-colors"
            >
              {note}
            </button>
          ) : null}
          {/* Add recipe / note buttons */}
          {recipes.length === 0 && !note && !editingNote ? (
            <div className="rounded-lg border border-dashed border-term-border hover:border-term-accent/40 text-sm text-term-text-muted transition-all py-2.5 flex items-center justify-center gap-3">
              <button
                onClick={() => onAdd(day, mealType)}
                className="hover:text-term-accent cursor-pointer transition-colors flex items-center gap-1 py-0.5"
              >
                <AddIcon width={12} height={12} />
                agregar
              </button>
              <span className="text-term-border">|</span>
              <button
                onClick={() => setEditingNote(true)}
                className="hover:text-term-accent cursor-pointer transition-colors flex items-center gap-1 py-0.5"
              >
                <EditIcon width={11} height={11} />
                nota
              </button>
            </div>
          ) : !editingNote ? (
            <div className="flex items-center gap-2 text-sm text-term-text-muted">
              <button
                onClick={() => onAdd(day, mealType)}
                className="hover:text-term-accent cursor-pointer transition-colors text-left px-1 py-1"
              >
                <AddIcon width={11} height={11} className="inline mr-0.5 -mt-px" />
                agregar
              </button>
              {!note && (
                <button
                  onClick={() => setEditingNote(true)}
                  className="hover:text-term-accent cursor-pointer transition-colors px-1 py-1"
                >
                  <EditIcon width={11} height={11} className="inline mr-0.5 -mt-px" />
                  nota
                </button>
              )}
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <Card
      className={`flex flex-col ${isToday ? "ring-2 ring-term-user/30 border-term-user/40" : ""}`}
      index={dayIndex}
    >
      {/* Day header */}
      <div
        className={`px-4 py-2.5 border-b border-term-border/40 ${isToday ? "bg-term-user/5" : ""}`}
      >
        <div className="flex items-baseline gap-2">
          <span
            className={`text-xl font-serif ${isToday ? "text-term-user font-semibold" : "text-term-text"}`}
          >
            {dayNum}
          </span>
          <span
            className={`text-xs font-medium uppercase tracking-wide ${isToday ? "text-term-user" : "text-term-text-muted"}`}
          >
            {DAYS_ES[day]}
          </span>
          {isToday && <span className="text-micro text-term-user font-medium ml-auto">hoy</span>}
        </div>
      </div>
      {/* Meals */}
      <div className="flex flex-col gap-4 px-4 py-3 flex-1">
        <MealSection
          mealLabel="Almuerzo"
          mealType="lunch"
          recipes={lunchRecipes}
          note={lunchNote}
        />
        <MealSection mealLabel="Cena" mealType="dinner" recipes={dinnerRecipes} note={dinnerNote} />
        {unplannedConsumptions.length > 0 && (
          <div>
            <div className="text-xs font-medium text-term-accent uppercase tracking-wide mb-1.5 flex items-center gap-1">
              <UnplannedIcon width={12} height={12} className="shrink-0" aria-hidden />
              Fuera del plan
            </div>
            <div className="flex flex-col gap-1.5">
              {unplannedConsumptions.map((row) => (
                <div
                  key={row.recipe_id}
                  className="group/unplanned rounded-lg border border-term-accent/25 bg-term-accent/8 px-2.5 py-1.5 flex items-center gap-2"
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-sm leading-snug text-term-text-dim">{row.recipe_name}</div>
                    <div className="text-micro text-term-accent font-medium mt-0.5">
                      {row.servings} {row.servings !== 1 ? "porciones" : "porción"}
                    </div>
                  </div>
                  <button
                    onClick={() => onDeleteConsumptions(row.consumption_ids)}
                    className="shrink-0 text-term-text-muted hover:text-term-error cursor-pointer transition-colors p-0.5 md:opacity-0 md:group-hover/unplanned:opacity-100"
                    aria-label="Eliminar consumo"
                  >
                    <DeleteIcon width={12} height={12} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}
